import { ButtonBorderless } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import classNames from "classnames"
import React, { FunctionComponent, ReactNode } from "react"

interface AlertContainerProps {
    type: "info" | "warning" | "error"
    variant?: "default" | "banner"
    title?: string
    body?: string | ReactNode
    icon?: ReactNode
    action?: ReactNode
    onClose?: () => void
    closeButtonOverride?: ReactNode
    className?: string
}

const styleMap: Record<AlertContainerProps["type"], string> = {
    error: "border-red-500 text-red-500 bg-red-510",
    info: "border-blue-700 text-gray-0 bg-blue-510",
    warning: "border-yellow-500 text-yellow-500 bg-yellow-510",
}

const iconMap: Record<AlertContainerProps["type"], ReactNode> = {
    error: (
        <span className={`text-${styleMap.error}`}>
            <Icons.Error />
        </span>
    ),

    info: (
        <span className={`text-${styleMap.info}`}>
            <Icons.Information />
        </span>
    ),

    warning: (
        <span className={`text-${styleMap.warning}`}>
            <Icons.Warning />
        </span>
    ),
}

const bannerIconColorMap: Record<AlertContainerProps["type"], string> = {
    error: "text-red-500",
    info: "text-blue-500",
    warning: "text-yellow-500",
}

const bannerIconMap: Record<
    AlertContainerProps["type"],
    React.ComponentProps<typeof Icons.SmallIcon>["Icon"]
> = {
    error: Icons.Error,
    info: Icons.Information,
    warning: Icons.Warning,
}

const AlertBanner: FunctionComponent<AlertContainerProps> = ({
    body,
    icon,
    title,
    type,
    action,
    onClose,
    closeButtonOverride,
    className,
}) => {
    const typeStyles = styleMap[type]

    return (
        <div
            className={classNames(
                `flex items-center gap-4 border ${typeStyles} px-5 py-3.5 rounded-md`,
                className
            )}
        >
            <div className="flex flex-1 min-w-0 items-center gap-2">
                {icon ?? (
                    <Icons.SmallIcon
                        Icon={bannerIconMap[type]}
                        className={classNames("shrink-0", bannerIconColorMap[type])}
                    />
                )}
                {title && <span className="break-words text-sm text-gray-0">{title}</span>}
                {body && <span className="break-words text-sm text-gray-0">{body}</span>}
                {action}
            </div>
            {closeButtonOverride ??
                (onClose && (
                    <button
                        type="button"
                        className="flex shrink-0 items-center gap-1.5 text-[12px] text-gray-300 hover:text-white"
                        onClick={onClose}
                    >
                        Dismiss
                        <Icons.SmallIcon Icon={Icons.XClose} />
                    </button>
                ))}
        </div>
    )
}

export const AlertContainer: FunctionComponent<AlertContainerProps> = (props) => {
    const {
        body,
        icon,
        title,
        type,
        variant = "default",
        onClose,
        closeButtonOverride,
        className,
    } = props

    if (variant === "banner") {
        return <AlertBanner {...props} />
    }

    const typeStyles = styleMap[type]
    const resolvedIcon = icon ?? iconMap[type]

    return (
        <div
            className={classNames(
                `flex flex-col gap-2 border ${typeStyles} p-2 rounded-md z-10`,
                className
            )}
        >
            <div className="flex justify-between items-center">
                <div className="flex gap-2 items-center">
                    <span>{resolvedIcon}</span> {/* Remove text-${mainColor} */}
                    <h5 className="text-gray-0">{title}</h5>
                </div>
                <div>
                    {closeButtonOverride ? (
                        <>{closeButtonOverride}</>
                    ) : (
                        <>
                            {onClose && (
                                <ButtonBorderless
                                    title="Dismiss"
                                    RightIcon={Icons.XClose}
                                    height="1.25rem"
                                    onClick={onClose}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
            {!!body && (
                <div className="w-full">
                    {typeof body === "string" ? (
                        <small className="text-gray-100">{body}</small>
                    ) : (
                        <>{body}</>
                    )}
                </div>
            )}
        </div>
    )
}
